var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, MenuToggle, Select, SelectList, SelectOption, TextInputGroup, TextInputGroupMain, TextInputGroupUtilities, } from '@patternfly/react-core';
import { TimesIcon } from '@patternfly/react-icons';
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { DEFAULT_POPPER_PROPS } from '../models/popper-default';
import { isDefined } from '../utils';
export const CREATE_NEW_ITEM = 'create-new-with-name';
export const Typeahead = ({ selectedItem, items: itemsProps, id, placeholder = 'Select or write an option', onChange, onCleanInput, 'aria-label': ariaLabel, 'data-testid': dataTestId, onCreate, onCreatePrefix, disabled = false, }) => {
    var _a, _b;
    const [filter, setFilter] = useState((_a = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.name) !== null && _a !== void 0 ? _a : '');
    const [inputValue, setInputValue] = useState((_b = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.name) !== null && _b !== void 0 ? _b : '');
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = useRef(null);
    const selectedOptionRef = useRef(null);
    const items = useMemo(() => {
        const isValueInArray = isDefined(itemsProps.find((item) => item.name === (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.name)));
        const localArray = itemsProps.slice();
        if (isValueInArray) {
            return localArray;
        }
        if ((selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.name) && (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.value)) {
            localArray.unshift({ name: selectedItem.name, value: selectedItem.value });
        }
        return localArray;
    }, [itemsProps, selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.name, selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.value]);
    useEffect(() => {
        if (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.name) {
            setInputValue(selectedItem.name);
        }
    }, [selectedItem]);
    const onItemChanged = useCallback((_event, name) => {
        var _a;
        if (name === CREATE_NEW_ITEM) {
            onCreate === null || onCreate === void 0 ? void 0 : onCreate(name, inputValue);
            setIsOpen(false);
            return;
        }
        else if (!isDefined(name)) {
            onChange === null || onChange === void 0 ? void 0 : onChange(undefined);
            setInputValue('');
            setIsOpen(false);
            return;
        }
        const localItem = items.find((item) => item.value === name);
        setInputValue((_a = localItem === null || localItem === void 0 ? void 0 : localItem.name) !== null && _a !== void 0 ? _a : '');
        setIsOpen(false);
        if (name !== (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.name)) {
            onChange === null || onChange === void 0 ? void 0 : onChange(localItem);
        }
    }, [onChange, items, selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.name, onCreate, inputValue]);
    const onToggleClick = (event) => __awaiter(void 0, void 0, void 0, function* () {
        event.stopPropagation();
        if (isOpen) {
            setIsOpen(false);
            return;
        }
        setFilter('');
        setIsOpen(true);
        requestAnimationFrame(() => {
            var _a, _b;
            (_a = selectedOptionRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
            (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        });
    });
    const onTextInputChange = (_event, value) => {
        setFilter(value);
        setInputValue(value);
    };
    const onTextInputClear = () => {
        var _a;
        setFilter('');
        setInputValue('');
        onCleanInput === null || onCleanInput === void 0 ? void 0 : onCleanInput();
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const filteredItems = useMemo(() => {
        const lowerFilterValue = filter.toLowerCase();
        return items.filter((item) => {
            var _a, _b;
            if (!lowerFilterValue) {
                return true;
            }
            const hasNameMatch = (_a = item.name) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(lowerFilterValue);
            const hasDescriptionMatch = (_b = item.description) === null || _b === void 0 ? void 0 : _b.toLowerCase().includes(lowerFilterValue);
            return hasNameMatch || hasDescriptionMatch;
        });
    }, [filter, items]);
    const toggle = (toggleRef) => (_jsx(MenuToggle, { isFullWidth: true, ref: toggleRef, onClick: onToggleClick, isExpanded: isOpen, isDisabled: disabled, variant: "typeahead", "aria-label": `${ariaLabel} toggle`, id: id, children: _jsxs(TextInputGroup, { isPlain: true, children: [_jsx(TextInputGroupMain, { autoComplete: "off", id: `${id}-typeahead-select-input`, "aria-label": ariaLabel, "data-testid": `${dataTestId}-typeahead-select-input`, ref: inputRef, placeholder: placeholder, onClick: onToggleClick, value: inputValue, onChange: onTextInputChange }), _jsx(TextInputGroupUtilities, { children: !!inputValue && (_jsx(Button, { variant: "plain", onClick: onTextInputClear, "aria-label": "Clear input value", "data-testid": `${dataTestId}__clear`, icon: _jsx(TimesIcon, { "aria-hidden": true }) })) })] }) }));
    return (_jsx(Select, { id: `${id}-typeahead-select`, "data-testid": `${dataTestId}-typeahead-select`, isScrollable: true, shouldFocusToggleOnSelect: true, isOpen: isOpen, selected: selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.name, onSelect: onItemChanged, toggle: toggle, onOpenChange: (isOpen) => setIsOpen(isOpen), popperProps: DEFAULT_POPPER_PROPS, children: _jsxs(SelectList, { children: [filteredItems.map((item) => (_jsx(SelectOption, { value: item.value, description: item.description, "aria-label": `option ${item.name.toLocaleLowerCase()}`, isSelected: item.name === (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.name), ref: item.name === (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.name) ? selectedOptionRef : undefined, children: item.name }, item.name))), filteredItems.length === 0 && _jsx(SelectOption, { isDisabled: true, children: "No items found" }), onCreate && (_jsxs(SelectOption, { value: CREATE_NEW_ITEM, "aria-label": `option ${CREATE_NEW_ITEM.toLocaleLowerCase()}`, children: ["Create new ", onCreatePrefix, " ", inputValue ? `'${inputValue}'` : ''] }))] }) }));
};
//# sourceMappingURL=Typeahead.js.map